(() => {
  const SELECTOR = '[data-rb-cart-upsell]';

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'en';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const imageSrc = (image) =>
    typeof image === 'string' ? image : image?.src || image?.url || '';

  const guideForHandle = (handle) => ({
    'maternity-leggings': 'RB-Cart-Size-Maternity',
    'postpartum-leggings': 'RB-Cart-Size-Postpartum',
    'restingbelly-c-section-compression-underwear': 'RB-Cart-Size-Csection',
    'restingbelly-maternity-pelvic-support-sling': 'RB-Cart-Size-Sling',
    'restingbelly-bamboo-maternity-amp-nursing-bra': 'RB-Cart-Size-Bra',
    'restingbelly-postpartum-compression-biker-shorts': 'RB-Cart-Size-Shorts',
    'restingbelly-c-section-recovery-briefs': 'RB-Cart-Size-Recovery-Briefs'
  })[handle] || '';

  const mountUpsell = async (container) => {
    if (!container || container.dataset.rbLoading || container.dataset.rbLoaded) return;
    const owningDrawer = container.closest('cart-drawer');
    if (owningDrawer && !owningDrawer.classList.contains('active') && !owningDrawer.hasAttribute('open')) return;

    const sourceProductId = container.dataset.sourceProductId;
    if (!sourceProductId) return;

    container.dataset.rbLoading = 'true';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartProductIds = new Set(
        (container.dataset.cartProductIds || '').split(',').filter(Boolean)
      );
      const manualHandles = (container.dataset.manualProductHandles || '')
        .split('|')
        .map((handle) => handle.trim())
        .filter(Boolean)
        .slice(0, 6);
      const autoFill = container.dataset.autoFill !== 'false';
      const recommendationCollection = container.dataset.recommendationCollection;
      if (!manualHandles.length && !autoFill) {
        container.dataset.rbLoaded = 'true';
        return;
      }
      const maxRecommendations = 24;
      const candidateHandles = [];
      const seenHandles = new Set();
      const addCandidate = (product) => {
        if (candidateHandles.length >= maxRecommendations || !product) return;
        const handle = String(product.handle || '').toLowerCase();
        const tags = Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || '');
        if (
          !handle ||
          seenHandles.has(handle) ||
          (product.id && cartProductIds.has(String(product.id))) ||
          ['poep', 'hakka', 'elektrische-borstkolf'].includes(handle) ||
          handle.includes('bundle') ||
          handle.includes('test') ||
          /not-for-individual-sale|bundle-gift|test-product|rb-internal-bundle-gift|supplier confirmation required|concept/i.test(tags)
        ) return;
        seenHandles.add(handle);
        candidateHandles.push(handle);
      };
      manualHandles.forEach((handle) => addCandidate({ handle }));

      const seenProductIds = new Set(cartProductIds);
      const recommendations = [];

      const addProduct = (product) => {
        if (!product || product.available === false || seenProductIds.has(String(product.id))) return;
        const handle = String(product.handle || '').toLowerCase();
        const tags = Array.isArray(product.tags) ? product.tags.join(' ') : String(product.tags || '');
        if (
          ['poep', 'hakka', 'elektrische-borstkolf'].includes(handle) ||
          handle.includes('bundle') ||
          handle.includes('test') ||
          /not-for-individual-sale|bundle-gift|test-product|rb-internal-bundle-gift|supplier confirmation required|concept/i.test(tags)
        ) return;
        const variant = product.variants?.find((candidate) => candidate.available);
        if (!variant) return;
        seenProductIds.add(String(product.id));
        recommendations.push({ product, variant });
      };

      if (autoFill && recommendationCollection && candidateHandles.length < maxRecommendations) {
        try {
          const response = await fetch(
            root + 'collections/' + encodeURIComponent(recommendationCollection) + '/products.json?limit=250',
            { headers: { Accept: 'application/json' } }
          );
          if (response.ok) {
            const payload = await response.json();
            (payload.products || []).forEach(addCandidate);
          }
        } catch (_) {
          // Keep the curated products when the collection cannot be loaded.
        }
      }

      if (autoFill && candidateHandles.length < maxRecommendations) {
        try {
          const response = await fetch(
            root + 'recommendations/products.json?product_id=' +
              encodeURIComponent(sourceProductId) +
              '&limit=10&intent=related',
            { headers: { Accept: 'application/json' } }
          );
          if (response.ok) {
            const payload = await response.json();
            (payload.products || []).forEach(addCandidate);
          }
        } catch (_) {
          // Keep the curated and category products when recommendations fail.
        }

        for (let page = 1; page <= 4 && candidateHandles.length < maxRecommendations; page += 1) {
          try {
            const response = await fetch(
              root + 'collections/all/products.json?limit=250&page=' + page,
              { headers: { Accept: 'application/json' } }
            );
            if (!response.ok) break;
            const payload = await response.json();
            const products = payload.products || [];
            products.forEach(addCandidate);
            if (products.length < 250) break;
          } catch (_) {
            break;
          }
        }
      }

      // All storefront Ajax product payloads use cents and image URLs. The collection
      // and recommendation endpoints above only establish the display order.
      const canonicalProducts = await Promise.all(candidateHandles.map(async (handle) => {
        try {
          const response = await fetch(
            root + 'products/' + encodeURIComponent(handle) + '.js',
            { headers: { Accept: 'application/json' } }
          );
          return response.ok ? response.json() : null;
        } catch (_) {
          return null;
        }
      }));
      canonicalProducts.forEach(addProduct);

      if (!recommendations.length) {
        container.dataset.rbLoaded = 'true';
        return;
      }

      const content = container.querySelector('[data-rb-cart-upsell-content]');
      if (!content) return;

      const labels = {
        add: container.dataset.upsellAdd || (window.RBUI?.add_to_cart || 'Add to cart'),
        adding: container.dataset.upsellAdding || (window.RBUI?.adding || 'Adding…'),
        added: container.dataset.upsellAdded || (window.RBUI?.added || 'Added'),
        retry: container.dataset.upsellRetry || (window.RBUI?.retry || 'Try again'),
        choose: container.dataset.upsellChoose || (window.RBUI?.choose_variant || 'Choose a variant'),
        chooseSize: container.dataset.upsellChooseSize || (window.RBUI?.choose_size || 'Choose a size'),
        guide: container.dataset.upsellGuide || (window.RBUI?.size_guide || 'Size guide')
      };
      const priceHtml = (variant) =>
        (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)
          ? '<s>' + formatMoney(Number(variant.compare_at_price)) + '</s>'
          : '') +
        '<span>' + formatMoney(Number(variant.price)) + '</span>';

      content.innerHTML = recommendations
        .map(({ product, variant }, index) => {
          const image = imageSrc(window.RBCartDrawerImageOverrides?.[String(product.id)] ||
            product.featured_image || product.images?.[0] || variant.featured_image);
          const needsChoice = (product.variants?.length || 0) > 1;
          const guideId = guideForHandle(product.handle);
          const optionId = 'rb-cart-upsell-option-' + index;
          return (
            '<div class="rb-cart-drawer-upsell__product" data-product-index="' + index + '">' +
              (image
                ? '<img class="rb-cart-drawer-upsell__image" src="' + escapeHtml(image) +
                  '" alt="' + escapeHtml(product.title) + '" width="128" height="128" loading="' +
                  (index < 2 ? 'eager' : 'lazy') + '">'
                : '<span class="rb-cart-drawer-upsell__image"></span>') +
              '<div class="rb-cart-drawer-upsell__details">' +
                '<p class="rb-cart-drawer-upsell__title">' + escapeHtml(product.title) + '</p>' +
                '<div class="rb-cart-drawer-upsell__price-row">' +
                  '<p class="rb-cart-drawer-upsell__price">' + priceHtml(variant) + '</p>' +
                  (guideId
                    ? '<button class="rb-cart-drawer-upsell__guide" type="button" popovertarget="' +
                      guideId + '">' + escapeHtml(labels.guide) + '</button>'
                    : '') +
                '</div>' +
              '</div>' +
              (needsChoice
                ? '<label class="visually-hidden" for="' + optionId + '">' +
                  escapeHtml(labels.choose + ' — ' + product.title) + '</label>' +
                  '<select class="rb-cart-drawer-upsell__select" id="' + optionId +
                  '" data-rb-upsell-variant><option value="">' +
                  escapeHtml(guideId ? labels.chooseSize : labels.choose) + '</option>' +
                  product.variants.filter((option) => option.available).map((option) =>
                    '<option value="' + escapeHtml(option.id) + '">' +
                    escapeHtml(option.public_title || option.title) + '</option>'
                  ).join('') + '</select>'
                : '') +
              '<button class="rb-cart-drawer-upsell__add" type="button" ' +
                (needsChoice ? 'disabled' : 'data-variant-id="' + escapeHtml(variant.id) + '"') +
                '>' + escapeHtml(labels.add) + '</button>' +
            '</div>'
          );
        })
        .join('');

      content.querySelectorAll('[data-rb-upsell-variant]').forEach((select) => {
        select.addEventListener('change', () => {
          const card = select.closest('.rb-cart-drawer-upsell__product');
          const addButton = card.querySelector('.rb-cart-drawer-upsell__add');
          const product = recommendations[Number(card.dataset.productIndex)]?.product;
          const selected = product?.variants.find((option) => String(option.id) === select.value);
          addButton.disabled = !selected;
          if (!selected) {
            delete addButton.dataset.variantId;
            return;
          }
          addButton.dataset.variantId = selected.id;
          card.querySelector('.rb-cart-drawer-upsell__price').innerHTML = priceHtml(selected);
          const selectedImage = imageSrc(selected.featured_image);
          const cardImage = card.querySelector('img.rb-cart-drawer-upsell__image');
          if (selectedImage && cardImage &&
            !window.RBCartDrawerImageOverrides?.[String(product.id)]) {
            cardImage.src = selectedImage;
          }
        });
      });

      content.querySelectorAll('button.rb-cart-drawer-upsell__add').forEach((addButton) => {
        addButton.addEventListener('click', async () => {
          if (addButton.disabled) return;
          addButton.disabled = true;
          const originalLabel = addButton.textContent;
          addButton.textContent = labels.adding;

          try {
            const addResponse = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: Number(addButton.dataset.variantId), quantity: 1 }],
                sections: ['cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname
              })
            });
            const state = await addResponse.json();
            if (!addResponse.ok) throw new Error(state.description || 'Unable to add');

            addButton.textContent = labels.added;
            addButton.setAttribute('aria-label', labels.added);
            await new Promise((resolve) => window.setTimeout(resolve, 300));
            addButton.closest('.rb-cart-drawer-upsell__product')?.remove();

            const drawer = document.querySelector('cart-drawer');
            if (drawer?.renderContents && state.sections) drawer.renderContents(state);
          } catch (_) {
            addButton.textContent = labels.retry;
            addButton.setAttribute('aria-label', labels.retry);
            await new Promise((resolve) => window.setTimeout(resolve, 900));
            addButton.disabled = false;
            addButton.textContent = originalLabel;
            addButton.setAttribute('aria-label', originalLabel);
          }
        });
      });

      container.hidden = false;
      container.dataset.rbLoaded = 'true';
    } catch (_) {
      container.dataset.rbLoaded = 'true';
    } finally {
      delete container.dataset.rbLoading;
    }
  };


  const installConfirmedCartSync = () => {
    if (window.__rbConfirmedCartSyncInstalled) return;
    window.__rbConfirmedCartSyncInstalled = true;

    const originalFetch = window.fetch.bind(window);
    const originalXhrOpen = window.XMLHttpRequest?.prototype?.open;
    const originalXhrSend = window.XMLHttpRequest?.prototype?.send;
    let refreshTimer;
    let refreshInFlight = false;
    let refreshQueued = false;
    let queuedAllowOpen = false;
    let scheduledAllowOpen = false;

    const cartDrawerIsOpen = () =>
      Boolean(document.querySelector('cart-drawer.active'));

    const isCartMutation = (urlValue, methodValue) => {
      const method = String(methodValue || 'GET').toUpperCase();
      if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return false;

      try {
        const url = new URL(String(urlValue || ''), window.location.origin);
        return (
          url.origin === window.location.origin &&
          /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname)
        );
      } catch (_) {
        return false;
      }
    };

    const isCartAddResource = (urlValue) => {
      try {
        const url = new URL(String(urlValue || ''), window.location.origin);
        return (
          url.origin === window.location.origin &&
          /\/cart\/add(?:\.js)?\/?$/.test(url.pathname)
        );
      } catch (_) {
        return false;
      }
    };

    const responseIncludesSections = (payload) =>
      Boolean(payload && typeof payload === 'object' && payload.sections && payload.sections['cart-drawer']);

    const LEGACY_POSTPARTUM_PRODUCT_ID = '16317929849181';
    const LEGACY_POSTPARTUM_GIFT_PRODUCT_ID = '16413232857437';
    const LEGACY_POSTPARTUM_GIFT_VARIANT_ID = 65565080912221;
    const LEGACY_ORIGINAL_MASK_PRODUCT_ID = '16282299007325';
    const LEGACY_DUO_CODE = 'RBPOSTDUO15';
    const LEGACY_TRIO_CODE = 'RBPOSTTRIO20';
    const LEGACY_FBT_CODE = 'RBPILLOW20';
    const LEGACY_FBT_BUNDLE_KEY = 'Pillow You May Also Like';
    const LEGACY_FBT_REQUIRED_PRODUCT_IDS = [
      '16275163480413',
      '16274774393181',
      '16282299007325'
    ];

    const propertyValue = (properties, key) =>
      String((properties && properties[key]) || '').trim();

    const collectAppliedDiscountCodes = (cart) => {
      const codes = new Set();
      const addCode = (value) => {
        const code = String(value || '').trim();
        if (code) codes.add(code.toUpperCase());
      };

      (cart.discount_codes || []).forEach((discount) => addCode(discount.code || discount.title));
      (cart.cart_level_discount_applications || []).forEach((discount) =>
        addCode(discount.code || discount.title)
      );
      (cart.items || []).forEach((item) => {
        [...(item.discount_allocations || []), ...(item.line_level_discount_allocations || [])].forEach((allocation) => {
          const application = allocation.discount_application || {};
          addCode(application.code || application.title);
        });
        (item.discounts || []).forEach((discount) => addCode(discount.code || discount.title));
      });

      return codes;
    };

    const collectExplicitDiscountCodes = (cart) => {
      const codes = new Set();
      const addCode = (value) => {
        const code = String(value || '').trim();
        if (code) codes.add(code.toUpperCase());
      };
      const addApplication = (application) => {
        const type = String(application?.type || '').toLowerCase();
        if (type === 'discount_code' || application?.code) {
          addCode(application.code || application.title);
        }
      };

      (cart.discount_codes || []).forEach((discount) =>
        addCode(discount.code || discount.title)
      );
      (cart.cart_level_discount_applications || []).forEach(addApplication);
      (cart.items || []).forEach((item) => {
        [...(item.discount_allocations || []), ...(item.line_level_discount_allocations || [])].forEach((allocation) =>
          addApplication(allocation.discount_application || {})
        );
      });

      return codes;
    };

    const readBundleConfig = (cart, sourceItem) => {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const configuredItem = sourceItem || items.find((item) => {
        const properties = item.properties || {};
        return (
          propertyValue(properties, '_rb_bundle_managed') === 'true' ||
          Boolean(propertyValue(properties, '_rb_bundle_qualifying_product_id')) ||
          propertyValue(properties, '_rb_bundle_gift') === 'true'
        );
      });
      const hasPostpartumItem = items.some(
        (item) => String(item.product_id || '') === LEGACY_POSTPARTUM_PRODUCT_ID
      );
      const hasPostpartumGift = items.some((item) => {
        const productId = String(item.product_id || '');
        const variantId = Number(item.variant_id || item.id || 0);
        return (
          propertyValue(item.properties || {}, '_rb_bundle_gift') === 'true' ||
          productId === LEGACY_POSTPARTUM_GIFT_PRODUCT_ID ||
          variantId === LEGACY_POSTPARTUM_GIFT_VARIANT_ID
        );
      });

      if (!configuredItem && !hasPostpartumItem && !hasPostpartumGift) return null;

      const properties = configuredItem?.properties || {};
      const qualifyingProductId =
        propertyValue(properties, '_rb_bundle_qualifying_product_id') ||
        LEGACY_POSTPARTUM_PRODUCT_ID;
      const configuredGiftVariantId = Number(
        propertyValue(properties, '_rb_bundle_gift_variant_id')
      );
      const isLegacyPostpartum =
        String(qualifyingProductId) === LEGACY_POSTPARTUM_PRODUCT_ID;
      const giftEnabledSetting = propertyValue(
        properties,
        '_rb_bundle_gift_enabled'
      );
      const giftEnabled =
        giftEnabledSetting === 'true' ||
        (giftEnabledSetting !== 'false' && configuredGiftVariantId > 0) ||
        (!configuredItem && isLegacyPostpartum);
      const giftVariantId =
        configuredGiftVariantId ||
        (isLegacyPostpartum ? LEGACY_POSTPARTUM_GIFT_VARIANT_ID : 0);

      return {
        key:
          propertyValue(properties, '_rb_bundle_key') ||
          'postpartum-leggings',
        qualifyingProductId,
        giftEnabled,
        giftVariantId,
        minimumQuantity:
          Number(propertyValue(properties, '_rb_bundle_gift_min_quantity')) || 3,
        duoCode:
          propertyValue(properties, '_rb_bundle_duo_code') ||
          (isLegacyPostpartum ? LEGACY_DUO_CODE : ''),
        trioCode:
          propertyValue(properties, '_rb_bundle_trio_code') ||
          (isLegacyPostpartum ? LEGACY_TRIO_CODE : ''),
        quadCode: propertyValue(properties, '_rb_bundle_quad_code') || '',
        giftLabel:
          propertyValue(properties, '_rb_bundle_gift_label') ||
          'Free RestingBelly Sleep Mask with 3+'
      };
    };

    const readBundleConfigs = (cart) => {
      const configs = new Map();
      (cart.items || []).forEach((item) => {
        const properties = item.properties || {};
        if (propertyValue(properties, '_rb_bundle_managed') !== 'true' &&
            !propertyValue(properties, '_rb_bundle_qualifying_product_id')) return;
        const config = readBundleConfig(cart, item);
        if (config) configs.set(config.key + ':' + config.qualifyingProductId, config);
      });
      if (!configs.size) {
        const legacy = readBundleConfig(cart);
        if (legacy) configs.set(legacy.key, legacy);
      }
      return [...configs.values()];
    };

    const requestedSections = (cart) => [...new Set([
      ...Object.keys(cart.sections || {}), 'cart-drawer', 'cart-icon-bubble'
    ])].slice(0, 5);

    const enforceBundleGiftRules = async (cart, config) => {
      if (!cart || !Array.isArray(cart.items)) return null;

      if (!config) return null;

      const qualifyingQuantity = cart.items.reduce((total, item) => {
        if (String(item.product_id || '') !== String(config.qualifyingProductId)) return total;
        return total + Number(item.quantity || 0);
      }, 0);
      const shouldHaveGift =
        config.giftEnabled &&
        Number(config.giftVariantId) > 0 &&
        qualifyingQuantity >= config.minimumQuantity;
      const giftLines = cart.items.filter((item) => {
        const properties = item.properties || {};
        const productId = String(item.product_id || '');
        const variantId = Number(item.variant_id || item.id || 0);
        const itemBundleKey = propertyValue(properties, '_rb_bundle_key');
        const isMatchingManagedGift =
          propertyValue(properties, '_rb_bundle_gift') === 'true' &&
          itemBundleKey === config.key;
        const isMatchingLegacyGift =
          String(config.qualifyingProductId) === LEGACY_POSTPARTUM_PRODUCT_ID &&
          (productId === LEGACY_POSTPARTUM_GIFT_PRODUCT_ID ||
            variantId === LEGACY_POSTPARTUM_GIFT_VARIANT_ID);
        return isMatchingManagedGift || isMatchingLegacyGift;
      });
      const correctGiftLine = giftLines.find(
        (item) => Number(item.variant_id || item.id || 0) === Number(config.giftVariantId)
      );

      const updates = {};
      giftLines.forEach((item) => {
        const isCorrectGift =
          Number(item.variant_id || item.id || 0) === Number(config.giftVariantId);
        if (!shouldHaveGift || !isCorrectGift) {
          updates[item.key] = 0;
        } else if (Number(item.quantity || 0) !== 1) {
          updates[item.key] = 1;
        }
      });

      const root = window.Shopify?.routes?.root || '/';
      let giftAdded = false;

      if (shouldHaveGift && !correctGiftLine) {
        const addResponse = await originalFetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify({
            items: [
              {
                id: Number(config.giftVariantId),
                quantity: 1,
                properties: {
                  '_RestingBelly gift': config.giftLabel,
                  '_rb_bundle_gift': 'true',
                  '_rb_bundle_gift_enabled': 'true',
                  '_rb_bundle_managed': 'true',
                  '_rb_bundle_key': config.key,
                  '_rb_bundle_qualifying_product_id': String(config.qualifyingProductId),
                  '_rb_bundle_gift_variant_id': String(config.giftVariantId),
                  '_rb_bundle_gift_min_quantity': String(config.minimumQuantity),
                  '_rb_bundle_duo_code': config.duoCode,
                  '_rb_bundle_trio_code': config.trioCode,
                  '_rb_bundle_gift_label': config.giftLabel
                }
              }
            ]
          })
        });
        giftAdded = addResponse.ok;
      }

      if (!giftAdded && !Object.keys(updates).length) return null;

      const updateBody = {
        updates,
        sections: requestedSections(cart),
        sections_url: window.location.pathname
      };

      const correctionResponse = await originalFetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify(updateBody)
      });
      if (!correctionResponse.ok) return null;
      return correctionResponse.json();
    };

    const readFbtConfig = (cart, sourceItem) => {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const configuredItem = sourceItem || items.find((item) => {
        const properties = item.properties || {};
        return (
          propertyValue(properties, '_rb_fbt_managed') === 'true' &&
          Boolean(propertyValue(properties, '_rb_fbt_discount_code'))
        );
      });
      const appliedCodes = collectAppliedDiscountCodes(cart);
      const hasLegacyBundleLine = items.some(
        (item) =>
          propertyValue(item.properties || {}, '_rb_bundle') ===
          LEGACY_FBT_BUNDLE_KEY
      );

      if (!configuredItem && !hasLegacyBundleLine && !appliedCodes.has(LEGACY_FBT_CODE)) {
        return null;
      }

      const properties = configuredItem?.properties || {};
      const requiredProductIds = propertyValue(
        properties,
        '_rb_fbt_required_product_ids'
      )
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      return {
        code:
          propertyValue(properties, '_rb_fbt_discount_code') ||
          LEGACY_FBT_CODE,
        requiredProductIds:
          requiredProductIds.length > 0
            ? requiredProductIds
            : LEGACY_FBT_REQUIRED_PRODUCT_IDS
      };
    };

    const readFbtConfigs = (cart) => {
      const configs = new Map();
      (cart.items || []).forEach((item) => {
        if (propertyValue(item.properties, '_rb_fbt_managed') !== 'true') return;
        const config = readFbtConfig(cart, item);
        if (config?.code) configs.set(config.code.toUpperCase(), config);
      });
      if (!configs.size) {
        const legacy = readFbtConfig(cart);
        if (legacy) configs.set(legacy.code.toUpperCase(), legacy);
      }
      return [...configs.values()];
    };

    const managedCodes = new Set([LEGACY_DUO_CODE, LEGACY_TRIO_CODE, LEGACY_FBT_CODE]);
    const rememberManagedCodes = (cart, codes) => {
      const storageKey = 'rb-bundle-codes:' + String(cart.token || '').split('?')[0];
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || '[]');
        if (Array.isArray(saved)) saved.forEach((code) => managedCodes.add(String(code).toUpperCase()));
      } catch (_) {}
      codes.filter(Boolean).forEach((code) => managedCodes.add(String(code).toUpperCase()));
      try { window.sessionStorage.setItem(storageKey, JSON.stringify([...managedCodes])); } catch (_) {}
    };

    const bundleDiscountCodes = (cart) => {
      const configs = readBundleConfigs(cart);
      const fbtConfigs = readFbtConfigs(cart);
      rememberManagedCodes(cart, [
        ...configs.flatMap((config) => [config.duoCode, config.trioCode, config.quadCode]),
        ...fbtConfigs.map((config) => config.code)
      ]);
      const nextCodes = new Set([...collectExplicitDiscountCodes(cart)].filter((code) => !managedCodes.has(code)));
      configs.forEach((config) => {
        const quantity = (cart.items || []).reduce((sum, item) =>
          String(item.product_id) === String(config.qualifyingProductId) &&
          propertyValue(item.properties, '_rb_bundle_gift') !== 'true'
            ? sum + Number(item.quantity || 0) : sum, 0);
        const code = quantity >= 4 && config.quadCode ? config.quadCode : quantity >= 3 ? config.trioCode : quantity >= 2 ? config.duoCode : '';
        if (code) nextCodes.add(code.toUpperCase());
      });
      const present = new Set((cart.items || []).filter((item) => Number(item.quantity) > 0)
        .map((item) => String(item.product_id)));
      fbtConfigs.forEach((config) => {
        if (config.requiredProductIds.length && config.requiredProductIds.every((id) => present.has(String(id)))) {
          nextCodes.add(config.code.toUpperCase());
        }
      });
      return nextCodes;
    };

    const enforceAllBundleRules = async (cart) => {
      if (!cart || !Array.isArray(cart.items)) return null;
      let effectiveCart = cart;
      let correction = null;
      bundleDiscountCodes(cart);
      for (const config of readBundleConfigs(cart)) {
        const giftCorrection = await enforceBundleGiftRules(effectiveCart, config);
        if (giftCorrection) {
          effectiveCart = giftCorrection;
          correction = giftCorrection;
        }
      }
      const nextCodes = bundleDiscountCodes(effectiveCart);
      const currentCodes = collectExplicitDiscountCodes(effectiveCart);
      const matches = nextCodes.size === currentCodes.size && [...nextCodes].every((code) => currentCodes.has(code));
      if (matches) return correction;
      const root = window.Shopify?.routes?.root || '/';
      const response = await originalFetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({
          discount: [...nextCodes].join(','),
          sections: requestedSections(cart),
          sections_url: window.location.pathname
        })
      });
      if (!response.ok) throw new Error('Bundle discounts could not be synchronized.');
      return response.json();
    };

    let cartWork = Promise.resolve();
    const queueCartWork = (task) => {
      const result = cartWork.then(task, task);
      cartWork = result.catch(() => {});
      return result;
    };

    const refreshCartDrawer = async (allowOpen = false) => {
      if (refreshInFlight) {
        refreshQueued = true;
        queuedAllowOpen = queuedAllowOpen || allowOpen;
        return;
      }

      refreshInFlight = true;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const sectionUrl = new URL(root, window.location.origin);
        sectionUrl.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
        const cartUrl = new URL('cart.js', sectionUrl);

        const [sectionResponse, cartResponse] = await Promise.all([
          originalFetch(sectionUrl.toString(), {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          }),
          originalFetch(cartUrl.toString(), {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          })
        ]);
        if (!sectionResponse.ok || !cartResponse.ok) return;

        const [sections, cart] = await Promise.all([
          sectionResponse.json(),
          cartResponse.json()
        ]);
        const correctedCart = await enforceAllBundleRules(cart);
        const effectiveCart = correctedCart || cart;
        const effectiveSections = correctedCart?.sections || sections;
        const drawer = document.querySelector('cart-drawer');
        const isOpen = cartDrawerIsOpen();

        // A closed drawer may only open after a confirmed add that left at
        // least one visible item in the cart. Background synchronisation stays
        // invisible and invalid gift states are corrected before rendering.
        if (!isOpen && (!allowOpen || Number(effectiveCart?.item_count || 0) <= 0)) return;

        if (drawer?.renderContents && effectiveSections?.['cart-drawer']) {
          drawer.renderContents({
            sections: effectiveSections,
            item_count: Number(effectiveCart?.item_count || 0)
          });

          // Keep the custom drawer heading in sync with the visible cart lines.
          // Hidden bundle trigger lines must never inflate the customer-facing count.
          window.requestAnimationFrame(() => {
            const visibleItemCount = Array.from(
              drawer.querySelectorAll('.cart-item:not([hidden]) .quantity__input')
            ).reduce(
              (total, input) => total + Number(input.value || 0),
              0
            );
            const countLabel = drawer.querySelector('.rb-cart-drawer__count');
            if (countLabel) countLabel.textContent = '(' + visibleItemCount + ')';
          });
        }
      } catch (_) {
        // The original Shopify cart request remains authoritative if a background refresh fails.
      } finally {
        refreshInFlight = false;
        if (refreshQueued) {
          const nextAllowOpen = queuedAllowOpen;
          refreshQueued = false;
          queuedAllowOpen = false;
          scheduleRefresh(nextAllowOpen);
        }
      }
    };

    const scheduleRefresh = (allowOpen = false) => {
      scheduledAllowOpen = scheduledAllowOpen || allowOpen;
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        const nextAllowOpen = scheduledAllowOpen;
        scheduledAllowOpen = false;
        queueCartWork(() => refreshCartDrawer(nextAllowOpen));
      }, 80);
    };

    const initialCart = originalFetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
      headers: { Accept: 'application/json' }, cache: 'no-store'
    }).then((response) => response.ok ? response.json() : null).then((cart) => {
      if (cart) bundleDiscountCodes(cart);
    }).catch(() => {});

    window.fetch = (...args) => {
      const input = args[0];
      const init = args[1] || {};
      const requestUrl = typeof input === 'string' || input instanceof URL ? input : input?.url;
      const requestMethod = init.method || input?.method || 'GET';
      const cartMutation = isCartMutation(requestUrl, requestMethod);
      const allowOpen = isCartAddResource(requestUrl);
      if (!cartMutation) return originalFetch(...args);
      return queueCartWork(async () => {
      await initialCart;
      const response = await originalFetch(...args);

      if (cartMutation && response.ok) {
        try {
          const payload = await response.clone().json();
          let ruleCart = payload;

          // Shopify's add response only contains the newly added lines. Read
          // the full cart before resolving the request so cross-line bundle
          // rules are correct in the first drawer render.
          if (allowOpen && (!Number.isFinite(Number(payload?.item_count)) || !payload?.token)) {
            const root = window.Shopify?.routes?.root || '/';
            const fullCartResponse = await originalFetch(root + 'cart.js', {
              method: 'GET',
              headers: { Accept: 'application/json' },
              credentials: 'same-origin',
              cache: 'no-store'
            });
            if (fullCartResponse.ok) ruleCart = await fullCartResponse.json();
          }

          const correctedCart = await enforceAllBundleRules(ruleCart);
          if (correctedCart) {
            return new Response(JSON.stringify(correctedCart), {
              status: response.status,
              statusText: response.statusText,
              headers: { 'Content-Type': 'application/json' }
            });
          }
          if (!responseIncludesSections(payload)) scheduleRefresh(allowOpen);
        } catch (_) {
          scheduleRefresh(allowOpen);
        }
      }

      return response;
      });
    };

    if (originalXhrOpen && originalXhrSend) {
      window.XMLHttpRequest.prototype.open = function (method, url, ...rest) {
        this.__rbCartMutation = isCartMutation(url, method);
        this.__rbCartMutationUrl = url;
        return originalXhrOpen.call(this, method, url, ...rest);
      };

      window.XMLHttpRequest.prototype.send = function (...args) {
        if (this.__rbCartMutation) {
          this.addEventListener(
            'load',
            () => {
              if (this.status < 200 || this.status >= 300) return;
              const allowOpen = isCartAddResource(this.__rbCartMutationUrl);
              try {
                const payload =
                  this.responseType === 'json' ? this.response : JSON.parse(this.responseText || '{}');
                if (!responseIncludesSections(payload)) scheduleRefresh(allowOpen);
              } catch (_) {
                scheduleRefresh(allowOpen);
              }
            },
            { once: true }
          );
        }

        return originalXhrSend.apply(this, args);
      };
    }
  };

  if (!window.rbLeggingsGuideEventsReady) {
    window.rbLeggingsGuideEventsReady = true;
    const updateGuide = (guide) => {
      if (!guide) return;
      const country = guide.querySelector('[data-rb-guide-country]')?.value || 'us';
      const unit = guide.querySelector('[data-rb-guide-unit].is-active')?.dataset.rbGuideUnit || 'cm';
      guide.querySelectorAll('[data-rb-country]').forEach((cell) => {
        cell.textContent = cell.getAttribute('data-' + country) || '';
      });
      guide.querySelectorAll('[data-rb-measure]').forEach((cell) => {
        cell.textContent = cell.getAttribute('data-' + unit) || '';
      });
    };
    document.addEventListener('change', (event) => {
      if (event.target.matches('[data-rb-guide-country]')) {
        updateGuide(event.target.closest('[data-rb-leggings-guide]'));
      }
    });
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rb-guide-unit]');
      const guide = button?.closest('[data-rb-leggings-guide]');
      if (!guide) return;
      guide.querySelectorAll('[data-rb-guide-unit]').forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      updateGuide(guide);
    });
  }

  installConfirmedCartSync();
  window.__rbCartBundleSync = true;

  const mountAll = () => document.querySelectorAll(SELECTOR).forEach(mountUpsell);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  const drawer = document.querySelector('cart-drawer');
  if (drawer) {
    const syncVisibleCartCount = () => {
      const visibleItemCount = Array.from(
        drawer.querySelectorAll('.cart-item:not([hidden]) .quantity__input')
      ).reduce((total, input) => total + Number(input.value || 0), 0);
      const countLabel = drawer.querySelector('.rb-cart-drawer__count');
      const nextLabel = '(' + visibleItemCount + ')';
      if (countLabel && countLabel.textContent.trim() !== nextLabel) {
        countLabel.textContent = nextLabel;
      }
    };

    syncVisibleCartCount();
    new MutationObserver(() => {
      if (drawer.classList.contains('active') || drawer.hasAttribute('open')) mountAll();
    }).observe(drawer, { attributes: true, attributeFilter: ['class', 'open'] });
    new MutationObserver(() =>
      window.requestAnimationFrame(() => {
        mountAll();
        syncVisibleCartCount();
      })
    ).observe(drawer, {
      childList: true,
      subtree: true
    });
  }
})();

